import { APP_TYPES, INVALID_IDP_ERROR_BANNER_KEYS } from '../../constants';
import BaseForgifyCustomizer from '../base-forge';
import athenaConfig from '../../config';
import { createWrapper } from '../../utils/react-utils';
import TermsContainer from '../../components/primary-auth/TermsContainer';
import {
  getAthenaOneSSOLoginTimedOutUrl,
  getAthenaOneSSOLoginUrl,
  getAthenaOneSsoReauthUrl,
  isAthenaOneWebClient,
  isAthenaOneWebOrMobileClient,
  isAthenaOneWebSessionTimedOut,
  isAthenaOneWebSso
} from '../../utils/app-utils';
import Header, { FontSize } from '../../components/common/Header';
import ForgotPasswordLink from '../../components/primary-auth/ForgotPasswordLink';
import MarketingContent from '../../components/primary-auth/MarketingContent';
import { closeOktaSession, hideWidgetForm, isPrimaryAuthController } from '../../utils/widget-facade';
import { applyMarketingFailoverLayout, applyMarketingLayout, removeMarketingLayout } from '../../utils/layout-utils';
import ExtrasSection from '../../components/primary-auth/ExtrasSection';
import AmplitudeUtils from '../../utils/amplitude-utils';
import { getAthenaOneParamsFromJwt, getBannerConfig } from '../../utils/banner-jwt-utils';
import { AlertTypes } from '@athena/forge';
import { getCustomLoginMetadata } from '../../utils/authorize-utils';
import InfoContainer from '../../components/common/InfoContainer';
import MutedText from '../../components/common/MutedText';
import React from 'react';
import { getLocalizedString } from '../../utils/i18n-utils';

const { clientIdsForBanner, marketingContentUrl } = athenaConfig;

export default abstract class BasePrimaryAuthCustomizer extends BaseForgifyCustomizer {
  protected abstract rememberMeSelector: string;
  protected bannerConfig: any = {};
  displayHeader = !isAthenaOneWebOrMobileClient();
  headerFontSize = 'xlarge' as FontSize;
  inputFieldsCustomClass = 'athena-input-form-field fe_u_padding--bottom-small';
  primaryButtonFullWidth = false;
  primaryButtonClassName = 'primary-auth-login-button';

  clearInfoBanner() {
    this.eventEmitter.emitEventToComponent({
      componentName: InfoContainer.displayName as string,
      eventName: 'set-info-banner',
      eventPayload: {},
    });
  }

  primaryButtonOnClick = async () => {
    const { athenaOneWebSso, athenaOneParams } = this.controllerContext;
    if (athenaOneWebSso && athenaOneParams?.SSOREAUTHPARAMS) {
      // Kill Okta session before starting SSO authentication as Okta is not able to update session
      //to newly authenticated if there is already a valid session
      await closeOktaSession();
      await AmplitudeUtils.logCustomEvent('SsoReauthenticationInitiated', { bannerType: athenaOneParams.BANNERTYPE });
      if (athenaOneParams.BANNERTYPE === 'LOGINTIMEDOUT') {
        window.location.href = getAthenaOneSSOLoginTimedOutUrl(athenaOneParams)!.toString();
      }
      else if (athenaOneParams.BANNERTYPE === 'LOGOUT' || athenaOneParams.BANNERTYPE === 'ERROR') {
        window.location.href = getAthenaOneSSOLoginUrl(athenaOneParams)!.toString();
      }
      else {
        window.open(getAthenaOneSsoReauthUrl(this.settings.clientId, athenaOneParams));
      }
    }
    else {
      this.clearInfoBanner();
    }
  };

  async shouldCustomize() {
    return isPrimaryAuthController(this.context?.controller);
  }

  private setBanner({ headerText, description, alertType }:
    { headerText: string; description: string; alertType?: AlertTypes }) {
    this.eventEmitter.emitEventToComponent({
      componentName: InfoContainer.displayName as string,
      eventName: 'set-info-banner',
      eventPayload: {
        headerText,
        description,
        alertType,
      },
    });
  }

  private addBanner(bannerDetails: any) {
    if ([ 'primary-auth', 'idp-discovery' ].includes(this.context.controller)) {
      setTimeout(() => this.setBanner(bannerDetails));
    }
  }

  private getBannerDetails() {
    return {
      headerText: getLocalizedString('portal.degradedstate.header'),
      description: getLocalizedString('portal.degradedstate.description'),
    };
  }

  private isDegradedStateBannerToBeShown() {
    return !this.settings.isAuthnWithLwaClientId && clientIdsForBanner.includes(this.settings.clientId);
  }

  private setDegradedStateBanner() {
    const { headerText, description } = this.getBannerDetails();
    this.addBanner({ headerText, description });
  }

  private addDegradedStateBanner() {
    if (!this.isDegradedStateBannerToBeShown()) return;
    this.setDegradedStateBanner();
  }


  async populateControllerContext() {
    const athenaOneParams = await getAthenaOneParamsFromJwt();
    this.controllerContext = {
      athenaOneParams,
      athenaOneWebSessionTimedOut: isAthenaOneWebSessionTimedOut(athenaOneParams),
      athenaOneWebSso: isAthenaOneWebSso(athenaOneParams),
    };
    this.suppressOktaClick = !!this.controllerContext.athenaOneWebSso;
  }

  private displayBannerBasedOnConfig() {
    const { athenaOneParams, athenaOneWebSso } = this.controllerContext;
    const bannerConfig = getBannerConfig(athenaOneParams, athenaOneWebSso);
    this.bannerConfig = bannerConfig;
    if (bannerConfig) {
      this.logInfoBannerAmplitudeEvents({
        ohswBannerKey: bannerConfig.bannerType,
        isSso: bannerConfig.isSso,
      });
      this.addBanner({
        headerText: bannerConfig.header,
        description: bannerConfig.description,
        alertType: bannerConfig.alertType,
      });
    }
  }


  async customize() {
    await this.populateControllerContext();
    await super.customize();
    this.displayBannerBasedOnConfig();
    AmplitudeUtils.logCustomEvent('PrimaryAuthLanding', getCustomLoginMetadata(this.bannerConfig));
    this.addAthenaOneWebCustomizations();
    this.addDegradedStateBanner();
    this.addTitleMessage();
    this.showTAndCText();
    this.hideCheckBox();
    this.hideFormElementsForIDPLoginError();
  }

  protected hideCheckBox() {
    const rememberMeContainer: JQuery<HTMLElement> = jQueryCourage(this.rememberMeSelector);
    this.hideComponent(rememberMeContainer[0]);
  }

  private addMarketingContent() {
    applyMarketingLayout();
    const marketingWrapper = createWrapper({ hidden: false });
    const marketingContainer = jQueryCourage('#marketing-container');
    marketingContainer.append(marketingWrapper);
    this.renderReactComponent({
      id: 'marketing',
      container: marketingWrapper,
      className: 'marketing-content',
      component: <MarketingContent marketingContentUrl={marketingContentUrl} />,
    });
    this.eventEmitter.on('marketing-content-failed', () => {
      removeMarketingLayout();
      applyMarketingFailoverLayout();
    });
  }

  protected addCopyrightText() {
    const authContentContainer = jQueryCourage('#okta-sign-in .auth-content');
    if (authContentContainer.length) {
      const copyrightText = createWrapper({ hidden: false });
      authContentContainer.after(copyrightText);
      const footerText = `© ${new Date().getFullYear()} athenahealth, Inc`;
      const component = (
        <div className={'fe_u_font-size--small fe_u_text-align--center'}>
          <MutedText text={footerText} />
        </div>
      );
      this.renderReactComponent({
        id: 'copyright',
        container: copyrightText,
        component,
      });
    }
  }

  private logInfoBannerAmplitudeEvents({ ohswBannerKey, isSso }: {ohswBannerKey: string; isSso?: boolean}) {
    if (ohswBannerKey === 'TIMEDOUT') {
      AmplitudeUtils.logCustomEvent('TimeoutLanding', { isSso });
    }
    else if (ohswBannerKey === 'LOGOUT') {
      AmplitudeUtils.logCustomEvent('LogoutLanding', { });
    }
    else if (ohswBannerKey === 'REFRESHTIMEOUT') {
      AmplitudeUtils.logCustomEvent('RefreshTimeoutLanding', { isSso });
    }
  }

  private addPrimaryAuthExtras() {
    const primaryAuthForm = jQueryCourage('form');
    const additionalLinksWrapper = createWrapper({ hidden: false });
    primaryAuthForm.append(additionalLinksWrapper);
    this.renderReactComponent({
      id: 'additional-links',
      container: additionalLinksWrapper,
      component: <ExtrasSection />,
    });
  }

  private addAthenaOneWebCustomizations() {
    if (!this.requestContext) return;
    if (!isAthenaOneWebClient()) return;
    if (marketingContentUrl) {
      this.addMarketingContent();
    }
    else {
      applyMarketingFailoverLayout();
    }
    this.addPrimaryAuthExtras();
    this.addForgotPasswordLink();
  }

  private addForgotPasswordLink() {
    const formContainer: JQuery<HTMLElement> = jQueryCourage('[data-se="o-form-content"]');
    const container = createWrapper({ hidden: false });
    formContainer.append(container);

    const component = <ForgotPasswordLink />;
    this.renderReactComponent({
      id: 'forgot-password-link',
      component,
      container,
    });
  }

  private getTitleMessage() {
    if (this.config?.language?.includes('es')) {
      return 'Inicia sesión para conectarte a';
    }
    return 'Log in to connect to';
  }

  protected addTitleMessage() {
    const titleMessage = this.getTitleMessage();
    if (!isAthenaOneWebOrMobileClient()) {
      const athenaHeaderContainer: JQuery<HTMLElement> = jQueryCourage('[data-se="o-form-head"]');
      const container = createWrapper({ hidden: false });
      athenaHeaderContainer.after(container);
      const component = <Header headerText={titleMessage} fontSize="small" fontColor="muted" fontWeight="regular" />;
      this.renderReactComponent({
        id: 'okta-form-titlemessage',
        component,
        container,
      });
    }
  }

  private showTAndCText() {
    const { requestContext, settings } = this;
    if (!requestContext) return;
    if (settings.appType !== APP_TYPES.ATHENANET_USER) return;
    const formContainer: JQuery<HTMLElement> = jQueryCourage('[data-se="o-form-content"]');
    const container = createWrapper({ hidden: false });
    formContainer.after(container);
    const component = <TermsContainer />;
    this.renderReactComponent({
      id: 'o-form-terms-container',
      component,
      container,
    });
  }

  protected shouldFormElementsBeHiddenForIDPLoginError() {
    const { athenaOneParams } = this.controllerContext;
    return athenaOneParams && INVALID_IDP_ERROR_BANNER_KEYS.includes(athenaOneParams.KEY);
  }

  private hideFormElementsForIDPLoginError() {
    if (this.shouldFormElementsBeHiddenForIDPLoginError()) {
      hideWidgetForm(true);
      AmplitudeUtils.logCustomEvent('InvalidIdentityProvider', { });
    }
  }
}
